import { type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export interface KpiCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /**
   * Dense variant — value text-2xl + label/sub text-xs, padding 16px.
   * Default 'default' keeps the original comfortable card.
   */
  density?: 'compact' | 'default';
}

export const KpiCard = ({
  label,
  value,
  sub,
  density = 'default',
  className,
  style,
  ...props
}: KpiCardProps) => {
  const isCompact = density === 'compact';
  return (
    <div
      className={cn('flex flex-col rounded-xl', isCompact ? 'gap-1 p-4' : 'gap-2 p-5', className)}
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--divider-strong)',
        boxShadow: 'var(--shadow-card-rest)',
        ...style,
      }}
      {...props}
    >
      <span
        className={cn(
          'font-medium uppercase tracking-[0.06em]',
          isCompact ? 'text-xs' : 'text-[12px]',
        )}
        style={{ color: 'var(--content-muted)' }}
      >
        {label}
      </span>
      <span
        className={cn(
          'font-semibold leading-none tracking-tight',
          isCompact ? 'text-2xl' : 'text-[28px]',
        )}
        style={{ color: 'var(--content-strong)' }}
      >
        {value}
      </span>
      {sub ? (
        <span
          className={isCompact ? 'text-xs' : 'text-[12px]'}
          style={{ color: 'var(--content-muted)' }}
        >
          {sub}
        </span>
      ) : null}
    </div>
  );
};

export interface KpiGridProps extends HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3 | 4 | 5;
  children: ReactNode;
}

export const KpiGrid = ({ columns = 4, children, className, style, ...props }: KpiGridProps) => {
  const gridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: 'var(--space-4)',
    ...style,
  };
  return (
    <div className={cn(className)} style={gridStyle} {...props}>
      {children}
    </div>
  );
};
